/**
* Locale copy pools for /{locale}/store/$product detail pages.
* Six locales, each with hand-written templates: lead variants, H2 pool,
* paragraph bank, FAQ pool, HowTo steps, meta title/desc, UI labels.
*
* Templates use {name}, {lo}, {hi}, {days} placeholders which are filled
* per product. Combined with a per-(locale, slug) PRNG in the generator,
* every one of the 32×6 = 192 mirror pages ships a unique combination.
*
* This is not machine translation — every string is authored so the
* hreflang cluster is safe against Google's cross-language duplicate
* de-duplication.
*/
import type { StoreLocale } from "./locale-copy";

export interface ProductLocaleCopy {
  lang: string;
  hreflang: string;
  h1: string;
  leads: string[];
  h2Pool: string[];
  paragraphs: string[];
  faqs: Array<{ q: string; a: string }>;
  howto: Array<{ tag: string; text: string }>;
  metaTitle: string;
  metaDesc: string;
  ui: {
    breadcrumbStore: string;
    breadcrumbHome: string;
    priceAndPackages: string;
    acceptedBy: string;
    howToOrder: string;
    faqs: string;
    stepOf: string; // "Step {i} · {tag}"
    ratingReviews: string; // "{v} · {n} reviews"
    naatiStamp: string;
    coc: string;
  };
}

export const PRODUCT_LOCALE_COPY: Record<StoreLocale, ProductLocaleCopy> = {
  en: {
    lang: "en", hreflang: "en",
    h1: "NAATI Certified Translation of {name} — quoted per document · 5-7 Business Days",
    leads: [
      "NAATI Certified translation of {name} produced by CPT-level translators registered with NAATI Australia. Every set carries a genuine blue NAATI stamp plus a Certificate of Currency and is accepted by DHA, VicRoads, Service NSW, AHPRA, USCIS, UKVI and IRCC without further legalisation. quoted per document per set with delivery in 5-7 business days.",
      "Order a NAATI Certified translation of {name} — three packages (Standard PDF / PDF + Hardcopy / Express 5-day) covering every migration, education, driver-licence and professional-registration workflow in Australia and the wider English-speaking market. Transparent quoted per document, no hidden per-page fees.",
      "If your Australian visa, university admission or driver-licence conversion asks for a NAATI Certified translation of {name}, we deliver it end-to-end: original intake, dedicated CPT translator, two-pass QA, blue NAATI stamp, Certificate of Currency, and courier of the hardcopy — inside a fixed 5-7 business-day window.",
    ],
    h2Pool: [
      "Why NAATI Certified for {name}",
      "Order and delivery workflow",
      "Authorities that accept our translations",
      "What to prepare before you order",
      "File formats and shipping options",
      "Standard price and turnaround",
      "Real-world use cases from clients",
      "NAATI vs Consular vs Sworn translation",
      "Common pitfalls to avoid",
      "Our translator team and credentials",
      "Quality guarantee and free revisions",
    ],
    paragraphs: [
      "Our {name} translation service is fully end-to-end: original intake, document-type analysis, assignment to a subject-specialist NAATI translator, two-pass review, stamping and issuance of a Certificate of Currency. The three-package structure at quoted per document lets you choose based on urgency and delivery method without any hidden line-item fees.",
      "Many clients ask why NAATI Certified matters for {name}. NAATI is the Australian government's translator accreditation body; every level of Australian officialdom accepts a NAATI translation directly, saving the fees and weeks of delay that consular or embassy re-certification would otherwise add.",
      "Every {name} project is handled by a NAATI-Certified Provisional Translator (CPT) or above who has passed the language-pair examination and is bound by the AUSIT Code of Ethics. Internal QA follows the ISO 17100 workflow for the translation industry — no shortcuts, no unchecked deliveries.",
      "Consistency of proper nouns matters: names, addresses and issuing authorities on {name} must match the spelling used elsewhere in your document set. Foreign officers routinely cross-compare, and a single unmatched character can trigger a request for correction — we align spellings across your entire submission bundle when you order more than one document.",
      "You will be informed at every stage over Line, Email or WhatsApp: order confirmed, translation in progress, QA in progress, stamped, dispatched. The status of your {name} translation is never a mystery and you never need to chase us for an update.",
      "Beyond the standard 5-7 day service we offer an Express 24-48 hour add-on for last-minute visa filings and university deadlines. The Express tier changes the queue priority only — the quality bar and the NAATI stamp itself are identical.",
      "A NAATI-stamped {name} translation has no expiry in itself — the stamp certifies accuracy at the moment of issue. Some receiving authorities however specify a translation issued within the last 3 or 6 months; check the requirement before you file so you don't ship a technically valid but administratively stale set.",
      "If you plan to use the {name} translation outside Australia — the United States, Canada, the UK, EU member states — you may additionally need an Apostille or Consular Legalization step. We can pair the NAATI translation with the appropriate legalisation channel so you receive one coordinated delivery.",
    ],
    faqs: [
      { q: "Is a NAATI Certified translation of {name} accepted by Australian embassies worldwide?", a: "Yes. NAATI is the Australian government's own accreditation. Every DFAT-affiliated post, DHA processing centre and Australian state authority accepts a translation carrying an in-date blue NAATI stamp directly." },
      { q: "Do you translate {name} into languages other than English?", a: "Yes. We work in Thai↔English by default; other language pairs such as Thai↔Chinese Simplified, Thai↔Japanese, Thai↔Korean and Thai↔Vietnamese are available on request with a matched NAATI stamp." },
      { q: "How long is the NAATI stamp on my {name} translation valid?", a: "The stamp itself does not expire. Some receiving bodies (e.g. UK NHS, certain US medical boards) require a translation issued within a defined window such as 3 or 6 months — always check the receiver's own rule." },
      { q: "Can you deliver the hardcopy of {name} to Australia?", a: "Yes. We ship registered post inside Thailand and DHL/UPS to Australia, the UK, the US and every country in the EU. Tracking is provided on dispatch." },
      { q: "What if the receiving authority asks for a correction on {name}?", a: "Corrections that arise from receiver clarification (e.g. an alternate transliteration) are free within 30 days of delivery. We reissue the file with a new stamp date and coordinate the reshipment if a hardcopy is required." },
    ],
    howto: [
      { tag: "Send", text: "Send a scan or clear phone photo of the original {name} to Line / Email — 300 dpi preferred." },
      { tag: "Confirm", text: "We confirm the package, price and delivery window inside two business hours." },
      { tag: "Translate", text: "A CPT-level NAATI translator produces the target-language file; a second linguist runs QA." },
      { tag: "Stamp", text: "The translator applies the blue NAATI stamp and issues the Certificate of Currency." },
      { tag: "Deliver", text: "You receive the PDF over Email plus, if selected, the hardcopy via registered courier." },
    ],
    metaTitle: "Order NAATI Translation Online — {name} | Thai Notary Law",
    metaDesc: "Order a NAATI certified translation of {name} online. Our staff review the document and send a quotation; acceptance is decided by the receiving authority.",
    ui: {
      breadcrumbStore: "Store", breadcrumbHome: "Home",
      priceAndPackages: "Price and packages",
      acceptedBy: "Accepted by",
      howToOrder: "How to order",
      faqs: "Frequently asked questions",
      stepOf: "Step {i} · {tag}",
      ratingReviews: "★ {v} · {n} reviews",
      naatiStamp: "Genuine NAATI Blue Stamp",
      coc: "Certificate of Currency",
    },
  },

  zh: {
    lang: "zh-CN", hreflang: "zh-CN",
    h1: "{name} NAATI 认证翻译 — 按文件报价 · 5-7 个工作日",
    leads: [
      "{name} 的 NAATI 认证翻译，由澳大利亚 NAATI 官方注册的 CPT 及以上级别译员完成。每份译件均盖有正品蓝色 NAATI 印章并附 Certificate of Currency，DHA、VicRoads、Service NSW、AHPRA、USCIS、UKVI、IRCC 均直接受理，无需再次公证。按文件报价 固定，5-7 个工作日交付。",
      "在线下单 {name} 的 NAATI 认证翻译 — 三档套餐（标准 PDF / PDF + 硬拷贝 / 5 天加急）覆盖澳大利亚移民、留学、驾照换发、专业注册等所有场景。按文件报价，无按页额外收费。",
      "澳洲签证、大学录取或驾照换发要求提供 {name} 的 NAATI 认证翻译？我们提供一站式服务：接稿、专属 CPT 译员翻译、双重审校、蓝色 NAATI 盖章、开具 Certificate of Currency、快递硬拷贝 — 全流程严守 5-7 个工作日窗口。",
    ],
    h2Pool: [
      "为什么 {name} 需要 NAATI 认证",
      "下单与交付流程",
      "接受本译件的官方机构",
      "下单前需要准备什么",
      "文件格式与配送方式",
      "标准价格与交付时限",
      "客户真实使用场景",
      "NAATI · 领事公证 · 宣誓翻译 三者对比",
      "常见风险与规避方法",
      "译员团队与资质",
      "质量保证与免费修订",
    ],
    paragraphs: [
      "我们的 {name} 翻译服务全流程一站式：接收原件、判定文件类型、指派对应专业方向的 NAATI 译员、双重审校、盖章、开具 Certificate of Currency。按文件报价 的三档结构让您可按紧急程度和交付方式自由选择，绝无按页隐藏费用。",
      "许多客户问为什么 {name} 必须 NAATI 认证。NAATI 是澳大利亚政府唯一的译员认证体系；澳洲各级官方机构均可直接采信 NAATI 译件，节省了领事再认证所需的费用与数周的等待时间。",
      "每一份 {name} 翻译均由 NAATI Certified Provisional Translator（CPT）或以上级别译员承担，其已通过语言对考试并受 AUSIT 职业道德守则约束。内部 QA 遵循翻译行业 ISO 17100 工作流 — 无捷径、无未审出件。",
      "专有名词一致性至关重要：{name} 中的姓名、地址、发证机关拼写必须与文件包中其他文件一致。海外办事人员会交叉比对，一个字符不同即可能被退回补正。当您同时下单多份文件时，我们会在整个提交包内统一拼写。",
      "全程通过 Line、Email 或 WhatsApp 同步进度：接单确认 → 翻译中 → 审校中 → 已盖章 → 已寄出。您的 {name} 译件状态永远透明，无需追问。",
      "在标准 5-7 天服务之外，我们还提供 24-48 小时加急选项，适用于临时签证提交与大学截止日期。加急仅调整队列优先级，质量标准与 NAATI 印章本身完全一致。",
      "NAATI 盖章的 {name} 译件本身无有效期 — 印章证明的是出具当刻的准确性。但部分接收方（如英国 NHS、美国部分医学委员会）会要求 3 或 6 个月内新出译件，请在提交前查看接收方规则。",
      "若您计划将 {name} 译件用于澳大利亚以外的国家（美、加、英、欧盟），可能需追加 Apostille 或领事认证。我们可为 NAATI 译件对接对应的认证通道，实现单次交付。",
    ],
    faqs: [
      { q: "全球澳大利亚使馆是否受理 {name} 的 NAATI 认证翻译？", a: "受理。NAATI 是澳大利亚政府自身的译员认证体系。所有 DFAT 相关领事馆、DHA 处理中心与澳洲各州机构均可直接采信盖有有效蓝色 NAATI 印章的译件。" },
      { q: "{name} 可以翻译成英语以外的语言吗？", a: "可以。默认为泰⇄英；泰⇄简中、泰⇄日、泰⇄韩、泰⇄越等语种可按需匹配对应的 NAATI 印章。" },
      { q: "{name} 的 NAATI 印章有效期多久？", a: "印章本身不设有效期。部分接收机构会要求 3 或 6 个月内新出的译件，请务必核对接收方自身的规则。" },
      { q: "{name} 的硬拷贝能否寄往澳大利亚？", a: "可以。泰国境内挂号寄送；国际件使用 DHL/UPS 寄往澳、英、美与欧盟各国，寄出后提供追踪单号。" },
      { q: "若对方要求对 {name} 进行修改怎么办？", a: "因接收方澄清（如替换译名）产生的修订，交付后 30 天内免费。我们将以新的盖章日期重出文件，并在需要硬拷贝时协助重寄。" },
    ],
    howto: [
      { tag: "发送", text: "通过 Line / Email 发送 {name} 原件的扫描件或清晰手机照片，建议 300 dpi。" },
      { tag: "确认", text: "两个工作小时内确认套餐、价格与交付时间。" },
      { tag: "翻译", text: "CPT 级 NAATI 译员完成翻译，第二位译员进行审校。" },
      { tag: "盖章", text: "由译员加盖蓝色 NAATI 印章并开具 Certificate of Currency。" },
      { tag: "交付", text: "Email 发送 PDF；如选购硬拷贝，则挂号快递寄出。" },
    ],
    metaTitle: "{name} NAATI 翻译在线下单 | Thai Notary Law",
    metaDesc: "在线提交 {name} 的 NAATI 认证翻译需求。工作人员审阅文件后提供报价；是否接受由收件机构决定。",
    ui: {
      breadcrumbStore: "商店", breadcrumbHome: "首页",
      priceAndPackages: "价格与套餐",
      acceptedBy: "受理机构",
      howToOrder: "下单流程",
      faqs: "常见问题",
      stepOf: "第 {i} 步 · {tag}",
      ratingReviews: "★ {v} · {n} 条评价",
      naatiStamp: "正品 NAATI 蓝色印章",
      coc: "Certificate of Currency",
    },
  },

  ja: {
    lang: "ja-JP", hreflang: "ja-JP",
    h1: "{name} の NAATI 認証翻訳 — 書類ごとにお見積り · 5〜7 営業日",
    leads: [
      "{name} の NAATI 認証翻訳を、オーストラリア NAATI 登録の CPT 以上の翻訳者が担当します。すべての納品物に本物のブルー NAATI スタンプと Certificate of Currency が付属し、DHA、VicRoads、Service NSW、AHPRA、USCIS、UKVI、IRCC が追加認証なしで受理します。書類ごとにお見積り の、5〜7 営業日で納品。",
      "{name} の NAATI 認証翻訳をオンラインでご注文いただけます — 3 プラン（標準 PDF / PDF + ハードコピー / 5 日エクスプレス）で、オーストラリアの移民・留学・免許切替・専門職登録すべてのワークフローを網羅。書類ごとにお見積り 、ページ課金なし。",
      "オーストラリアのビザ、大学出願、免許切替で {name} の NAATI 認証翻訳を求められましたか？受付から専任 CPT 翻訳者による翻訳、二重チェック、ブルースタンプ押印、Certificate of Currency 発行、ハードコピー配送まで、5〜7 営業日のウィンドウで完結します。",
    ],
    h2Pool: [
      "{name} に NAATI 認証が必要な理由",
      "注文から納品までの流れ",
      "本翻訳を受理する機関",
      "ご注文前にご準備いただくもの",
      "ファイル形式と配送方法",
      "標準価格と納期",
      "お客様の実際のご利用例",
      "NAATI・領事認証・宣誓翻訳の比較",
      "提出前に避けたい落とし穴",
      "翻訳者チームと資格",
      "品質保証と無料修正",
    ],
    paragraphs: [
      "{name} の翻訳は完全ワンストップ：原本受領、書類種別判定、専門分野の NAATI 翻訳者へのアサイン、二重レビュー、押印、Certificate of Currency 発行。書類ごとにお見積り の 3 段プラン構成で、緊急度と配送方法に応じてお選びいただけ、ページ単位の隠れた料金はありません。",
      "{name} になぜ NAATI 認証が必要かというご質問をよくいただきます。NAATI はオーストラリア政府唯一の翻訳者認証機関であり、オーストラリアの各級官庁は NAATI 翻訳を直接受理するため、領事再認証にかかる費用と数週間の遅延を回避できます。",
      "{name} の案件はすべて NAATI Certified Provisional Translator（CPT）以上の翻訳者が担当し、AUSIT 倫理綱領を遵守します。社内 QA は翻訳業界の ISO 17100 ワークフローに準拠 — ショートカットなし、未チェック納品なしです。",
      "固有名詞の一貫性が重要です：{name} 上の氏名、住所、発行機関の表記は、ご提出書類一式の他書類と一致していなければなりません。海外の担当者は必ず照合し、1 文字違うだけで補正要請につながります。複数書類ご注文時は提出パッケージ全体で表記を統一します。",
      "Line、メール、WhatsApp で進捗を都度ご報告：受注確定 → 翻訳中 → チェック中 → 押印済 → 発送済。{name} 翻訳のステータスが不明になることはなく、催促のご連絡は不要です。",
      "標準の 5〜7 日サービスに加え、24〜48 時間のエクスプレスオプションも提供。直前のビザ提出や大学の締切に対応します。エクスプレスはキュー優先順位のみを変更し、品質基準と NAATI スタンプ自体は同一です。",
      "NAATI 押印の {name} 翻訳はスタンプ自体に有効期限はなく、発行時点の正確性を証明します。ただし英国 NHS や米国の一部医療委員会など、3 か月または 6 か月以内発行の指定がある場合があるため、提出前に受領機関の規定を必ずご確認ください。",
      "{name} の翻訳をオーストラリア以外（米国、カナダ、英国、EU 各国）で使用する場合、追加で Apostille または領事認証が必要になることがあります。NAATI 翻訳と対応する認証チャネルを組み合わせ、一括納品可能です。",
    ],
    faqs: [
      { q: "世界各地のオーストラリア大使館で {name} の NAATI 認証翻訳は受理されますか？", a: "はい。NAATI はオーストラリア政府自身の認証制度であり、DFAT 傘下の在外公館、DHA 処理センター、豪州各州機関はいずれも有効なブルースタンプ付き翻訳を直接受理します。" },
      { q: "{name} を英語以外の言語に翻訳できますか？", a: "はい。標準はタイ⇄英ですが、タイ⇄簡体中、タイ⇄日、タイ⇄韓、タイ⇄越などのペアも対応する NAATI スタンプ付きで提供可能です。" },
      { q: "{name} の NAATI スタンプの有効期間は？", a: "スタンプ自体に期限はありません。ただし英国 NHS や米国の一部医療委員会など、発行から 3 か月または 6 か月以内の翻訳を求める受領機関もあるため、必ず受領側の規定をご確認ください。" },
      { q: "{name} のハードコピーをオーストラリアに配送できますか？", a: "可能です。タイ国内は書留、豪州・英国・米国・EU 各国へは DHL/UPS で発送し、追跡番号を発行時にお渡しします。" },
      { q: "{name} の修正要請が来た場合は？", a: "受領機関のご指摘（別表記など）による修正は納品後 30 日以内は無料です。新しい押印日で再発行し、ハードコピー再配送も手配します。" },
    ],
    howto: [
      { tag: "送付", text: "Line / メールで {name} 原本のスキャンまたは鮮明なスマホ写真（300 dpi 推奨）を送信。" },
      { tag: "確認", text: "2 営業時間以内にプラン、価格、納期を確定。" },
      { tag: "翻訳", text: "CPT 級 NAATI 翻訳者が翻訳、別の翻訳者が QA を実施。" },
      { tag: "押印", text: "翻訳者がブルー NAATI スタンプを押印し、Certificate of Currency を発行。" },
      { tag: "納品", text: "メールで PDF を送付。ハードコピー選択時は書留配送。" },
    ],
    metaTitle: "NAATI 翻訳注文 — {name} | Thai Notary Law",
    metaDesc: "{name} の NAATI 認証翻訳をオンラインで依頼。スタッフが書類を確認してお見積りします。受理の可否は提出先機関が判断します。",
    ui: {
      breadcrumbStore: "ストア", breadcrumbHome: "ホーム",
      priceAndPackages: "価格とパッケージ",
      acceptedBy: "受理機関",
      howToOrder: "ご注文方法",
      faqs: "よくあるご質問",
      stepOf: "ステップ {i} · {tag}",
      ratingReviews: "★ {v} · {n} 件のレビュー",
      naatiStamp: "本物の NAATI ブルースタンプ",
      coc: "Certificate of Currency",
    },
  },

  ko: {
    lang: "ko-KR", hreflang: "ko-KR",
    h1: "{name} NAATI 공인 번역 — 서류별 견적 · 영업일 5-7일",
    leads: [
      "{name}의 NAATI 공인 번역을 호주 NAATI 등록 CPT 이상 번역사가 담당합니다. 모든 세트에 정품 블루 NAATI 스탬프와 Certificate of Currency가 포함되며 DHA, VicRoads, Service NSW, AHPRA, USCIS, UKVI, IRCC가 추가 인증 없이 접수합니다. 서류별 견적 , 영업일 5-7일 납품.",
      "{name}의 NAATI 공인 번역을 온라인으로 주문하세요 — 3개 패키지(스탠다드 PDF / PDF + 하드카피 / 5일 익스프레스)로 호주 이민·유학·면허 교환·전문직 등록 전 워크플로우를 포괄합니다. 서류별 견적 , 페이지 과금 없음.",
      "호주 비자, 대학 지원, 면허 교환에서 {name}의 NAATI 공인 번역이 요구되나요? 원본 접수부터 전담 CPT 번역사 배정, 이중 검수, 블루 스탬프 날인, Certificate of Currency 발행, 하드카피 배송까지 5-7 영업일 안에 완결합니다.",
    ],
    h2Pool: [
      "{name}에 NAATI 공인이 필요한 이유",
      "주문 및 납품 절차",
      "본 번역물을 접수하는 기관",
      "주문 전 준비사항",
      "파일 형식과 배송 방법",
      "표준 가격과 납기",
      "고객 실제 사용 사례",
      "NAATI · 영사 공증 · 선서 번역 비교",
      "제출 전 주의사항",
      "번역사 팀과 자격",
      "품질 보증과 무료 수정",
    ],
    paragraphs: [
      "저희 {name} 번역 서비스는 완전 원스톱입니다: 원본 접수, 문서 유형 판정, 전문 분야별 NAATI 번역사 배정, 이중 검토, 날인, Certificate of Currency 발행. 서류별 견적의 3단 패키지 구성으로 긴급도와 배송 방식에 따라 선택 가능하며, 페이지 단위 숨은 비용이 없습니다.",
      "{name}에 왜 NAATI 공인이 필요한지 자주 문의를 받습니다. NAATI는 호주 정부 유일의 번역사 인증 기관으로, 호주 각급 관청이 NAATI 번역물을 직접 접수하므로 영사 재인증에 드는 비용과 수 주의 지연을 절감할 수 있습니다.",
      "모든 {name} 프로젝트는 NAATI Certified Provisional Translator(CPT) 이상 번역사가 담당하며 AUSIT 윤리강령을 준수합니다. 내부 QA는 번역 업계 ISO 17100 워크플로우를 따릅니다 — 지름길 없음, 미검수 납품 없음.",
      "고유명사 일관성이 중요합니다: {name}에 기재된 이름·주소·발급 기관 표기가 제출 문서 세트의 다른 문서와 일치해야 합니다. 해외 담당자는 반드시 대조 확인하며 단 한 글자만 달라도 보정 요청으로 이어질 수 있습니다. 여러 문서를 동시 주문하시면 제출 패키지 전체에서 표기를 통일합니다.",
      "Line, 이메일, WhatsApp으로 진행 상황을 단계별 안내: 주문 확정 → 번역 중 → 검수 중 → 날인 완료 → 발송 완료. {name} 번역 상태를 몰라 재촉 문의를 하실 필요가 없습니다.",
      "표준 5-7일 서비스 외 24-48시간 익스프레스 옵션도 제공, 임박한 비자 제출과 대학 마감에 대응합니다. 익스프레스는 대기열 우선순위만 변경하며 품질 기준과 NAATI 스탬프 자체는 동일합니다.",
      "NAATI 날인된 {name} 번역물은 스탬프 자체에 유효기간이 없으며 발급 시점의 정확성을 증명합니다. 단, 영국 NHS나 미국 일부 의료위원회처럼 3개월 또는 6개월 이내 발급을 요구하는 기관이 있으므로 제출 전 접수 기관의 규정을 반드시 확인하세요.",
      "{name} 번역물을 호주 이외 국가(미국·캐나다·영국·EU 각국)에서 사용할 경우 Apostille 또는 영사 인증이 추가로 필요할 수 있습니다. NAATI 번역과 해당 인증 채널을 연계해 단일 납품이 가능합니다.",
    ],
    faqs: [
      { q: "세계 각지 호주 대사관에서 {name}의 NAATI 공인 번역을 접수합니까?", a: "네. NAATI는 호주 정부 자체 인증 제도이며 DFAT 산하 재외공관, DHA 처리 센터, 호주 각 주 기관 모두 유효한 블루 스탬프가 있는 번역물을 직접 접수합니다." },
      { q: "{name}을 영어 외 다른 언어로 번역할 수 있습니까?", a: "가능합니다. 기본은 태국어⇄영어이며 태국어⇄중국어 간체, 태국어⇄일본어, 태국어⇄한국어, 태국어⇄베트남어 등도 해당 NAATI 스탬프와 함께 제공됩니다." },
      { q: "{name}의 NAATI 스탬프 유효기간은?", a: "스탬프 자체는 무기한이나 영국 NHS나 미국 일부 의료위원회처럼 3개월 또는 6개월 이내 발급 번역물을 요구하는 접수 기관이 있으므로 반드시 접수 기관 규정을 확인하세요." },
      { q: "{name} 하드카피를 호주로 배송할 수 있습니까?", a: "가능합니다. 태국 국내는 등기, 호주·영국·미국·EU 각국은 DHL/UPS로 발송하며 발송 시 추적 번호를 제공합니다." },
      { q: "{name}에 수정 요청이 온 경우?", a: "접수 기관의 안내(예: 대체 표기)에 따른 수정은 납품 후 30일 이내 무료입니다. 새 날인 일자로 재발행하며 하드카피가 필요한 경우 재배송을 조율합니다." },
    ],
    howto: [
      { tag: "전송", text: "Line / 이메일로 {name} 원본 스캔본 또는 선명한 휴대폰 사진(300 dpi 권장) 전송." },
      { tag: "확인", text: "2 영업시간 이내 패키지, 가격, 납기 확정." },
      { tag: "번역", text: "CPT급 NAATI 번역사가 번역, 다른 언어 전문가가 QA 수행." },
      { tag: "날인", text: "번역사가 블루 NAATI 스탬프 날인 및 Certificate of Currency 발행." },
      { tag: "납품", text: "이메일로 PDF 전송, 하드카피 선택 시 등기 배송." },
    ],
    metaTitle: "NAATI 번역 주문 — {name} | Thai Notary Law",
    metaDesc: "{name} NAATI 공인 번역을 온라인으로 의뢰하세요. 담당자가 서류를 검토한 후 견적을 드리며, 접수 여부는 제출 기관이 결정합니다.",
    ui: {
      breadcrumbStore: "스토어", breadcrumbHome: "홈",
      priceAndPackages: "가격 및 패키지",
      acceptedBy: "접수 기관",
      howToOrder: "주문 방법",
      faqs: "자주 묻는 질문",
      stepOf: "{i}단계 · {tag}",
      ratingReviews: "★ {v} · 리뷰 {n}건",
      naatiStamp: "정품 NAATI 블루 스탬프",
      coc: "Certificate of Currency",
    },
  },

  vi: {
    lang: "vi-VN", hreflang: "vi-VN",
    h1: "Dịch NAATI công chứng {name} — báo giá theo từng tài liệu · 5-7 ngày làm việc",
    leads: [
      "Dịch NAATI công chứng {name} do biên dịch viên cấp CPT trở lên đăng ký với NAATI Úc thực hiện. Mỗi bộ có dấu NAATI xanh thật kèm Certificate of Currency, được DHA, VicRoads, Service NSW, AHPRA, USCIS, UKVI, IRCC tiếp nhận trực tiếp không cần công chứng lại. báo giá theo từng tài liệu , giao trong 5-7 ngày làm việc.",
      "Đặt dịch NAATI công chứng {name} — ba gói (Standard PDF / PDF + Bản in / Express 5 ngày) bao trọn mọi quy trình di trú, du học, đổi bằng lái và đăng ký hành nghề chuyên môn tại Úc. báo giá theo từng tài liệu, không tính phí theo trang.",
      "Hồ sơ xin visa Úc, nhập học đại học hay đổi bằng lái yêu cầu bản dịch NAATI công chứng {name}? Chúng tôi thực hiện trọn gói: nhận bản gốc, biên dịch viên CPT chuyên trách, QA hai bước, đóng dấu NAATI xanh, phát hành Certificate of Currency, giao bản in — trong đúng cửa sổ 5-7 ngày làm việc.",
    ],
    h2Pool: [
      "Vì sao {name} cần NAATI công chứng",
      "Quy trình đặt và giao hàng",
      "Các cơ quan tiếp nhận bản dịch",
      "Cần chuẩn bị gì trước khi đặt",
      "Định dạng file và phương thức giao",
      "Giá và thời gian tiêu chuẩn",
      "Tình huống sử dụng thực tế của khách",
      "So sánh NAATI, công chứng lãnh sự và dịch tuyên thệ",
      "Những sai lầm cần tránh trước khi nộp",
      "Đội ngũ biên dịch và chứng chỉ",
      "Cam kết chất lượng và sửa miễn phí",
    ],
    paragraphs: [
      "Dịch vụ dịch {name} của chúng tôi trọn gói từ đầu đến cuối: tiếp nhận bản gốc, phân loại tài liệu, giao cho biên dịch viên NAATI chuyên ngành, rà soát hai bước, đóng dấu và phát hành Certificate of Currency. Cấu trúc ba gói báo giá theo từng tài liệu cho phép bạn chọn theo mức độ khẩn và phương thức giao, tuyệt đối không có phí ẩn theo trang.",
      "Nhiều khách hàng hỏi vì sao {name} phải NAATI công chứng. NAATI là hệ thống chứng nhận biên dịch viên duy nhất của Chính phủ Úc; mọi cấp cơ quan Úc tiếp nhận bản dịch NAATI trực tiếp, tiết kiệm được phí và nhiều tuần chờ đợi so với công chứng lãnh sự.",
      "Mọi hồ sơ {name} đều do biên dịch viên NAATI Certified Provisional Translator (CPT) trở lên xử lý, tuân thủ Bộ Quy tắc Đạo đức AUSIT. QA nội bộ theo quy trình ISO 17100 của ngành dịch thuật — không có đường tắt, không có bản chưa duyệt.",
      "Tính nhất quán của danh từ riêng rất quan trọng: tên, địa chỉ và cơ quan cấp trên {name} phải trùng với cách viết ở các giấy tờ khác trong bộ hồ sơ. Cán bộ nước ngoài luôn đối chiếu, và chỉ cần một ký tự lệch cũng có thể bị yêu cầu chỉnh sửa. Khi bạn đặt nhiều giấy tờ cùng lúc, chúng tôi thống nhất cách viết trên toàn bộ hồ sơ.",
      "Chúng tôi báo tiến độ qua Line, Email hoặc WhatsApp theo từng bước: xác nhận đơn → đang dịch → đang QA → đã đóng dấu → đã gửi. Trạng thái bản dịch {name} luôn minh bạch, không phải hỏi lại.",
      "Ngoài mức 5-7 ngày tiêu chuẩn, chúng tôi có gói Express 24-48 giờ dành cho hồ sơ visa và mốc đại học sát hạn. Express chỉ thay đổi thứ tự hàng đợi — tiêu chuẩn chất lượng và dấu NAATI thực tế đều như nhau.",
      "Bản dịch {name} đóng dấu NAATI bản thân không có hạn sử dụng — dấu chứng nhận độ chính xác tại thời điểm phát hành. Tuy nhiên một số nơi nhận (như NHS Anh, một số hội đồng y khoa Mỹ) yêu cầu bản dịch phát hành trong vòng 3 hoặc 6 tháng — hãy kiểm tra quy định của nơi nhận trước khi nộp.",
      "Nếu bạn dùng bản dịch {name} ngoài Úc (Mỹ, Canada, Anh, các nước EU), có thể cần bổ sung Apostille hoặc công chứng lãnh sự. Chúng tôi có thể ghép bản dịch NAATI với kênh hợp pháp hóa phù hợp để bạn nhận một lần giao trọn gói.",
    ],
    faqs: [
      { q: "Các Đại sứ quán Úc trên thế giới có tiếp nhận bản dịch NAATI công chứng của {name} không?", a: "Có. NAATI chính là hệ thống chứng nhận của Chính phủ Úc. Mọi cơ quan đại diện DFAT, trung tâm xử lý DHA và cơ quan tiểu bang Úc đều tiếp nhận bản dịch có dấu NAATI xanh còn hiệu lực." },
      { q: "{name} có dịch được sang ngôn ngữ khác ngoài tiếng Anh không?", a: "Có. Mặc định là Thái⇄Anh; các cặp Thái⇄Trung giản thể, Thái⇄Nhật, Thái⇄Hàn, Thái⇄Việt cũng có sẵn với dấu NAATI tương ứng." },
      { q: "Dấu NAATI trên {name} có hạn bao lâu?", a: "Bản thân dấu không có hạn. Một số cơ quan nhận (NHS Anh, một số hội đồng y khoa Mỹ) yêu cầu bản dịch phát hành trong 3 hoặc 6 tháng — hãy kiểm tra quy định của nơi nhận." },
      { q: "Có gửi bản in {name} sang Úc được không?", a: "Có. Trong nước Thái Lan gửi bảo đảm, quốc tế dùng DHL/UPS đi Úc, Anh, Mỹ, các nước EU. Mã tra cứu được cấp khi gửi hàng." },
      { q: "Nếu nơi nhận yêu cầu chỉnh sửa {name} thì sao?", a: "Chỉnh sửa phát sinh từ hướng dẫn của nơi nhận (ví dụ đổi cách phiên âm) miễn phí trong 30 ngày kể từ khi giao. Chúng tôi xuất lại với ngày đóng dấu mới và hỗ trợ gửi lại bản in nếu cần." },
    ],
    howto: [
      { tag: "Gửi", text: "Gửi bản scan hoặc ảnh điện thoại rõ nét (khuyến nghị 300 dpi) của {name} qua Line / Email." },
      { tag: "Xác nhận", text: "Chúng tôi xác nhận gói, giá và thời gian giao trong vòng 2 giờ làm việc." },
      { tag: "Dịch", text: "Biên dịch viên NAATI cấp CPT thực hiện, biên dịch viên khác chạy QA." },
      { tag: "Đóng dấu", text: "Biên dịch viên đóng dấu NAATI xanh và phát hành Certificate of Currency." },
      { tag: "Giao", text: "Gửi PDF qua Email; nếu chọn bản in, gửi bảo đảm/chuyển phát nhanh." },
    ],
    metaTitle: "Đặt dịch NAATI trực tuyến — {name} | Thai Notary Law",
    metaDesc: "Gửi yêu cầu dịch NAATI {name} trực tuyến. Nhân viên xem tài liệu và gửi báo giá; việc chấp nhận do cơ quan tiếp nhận quyết định.",
    ui: {
      breadcrumbStore: "Cửa hàng", breadcrumbHome: "Trang chủ",
      priceAndPackages: "Giá và gói",
      acceptedBy: "Cơ quan tiếp nhận",
      howToOrder: "Cách đặt",
      faqs: "Câu hỏi thường gặp",
      stepOf: "Bước {i} · {tag}",
      ratingReviews: "★ {v} · {n} đánh giá",
      naatiStamp: "Dấu NAATI xanh chính hãng",
      coc: "Certificate of Currency",
    },
  },

  id: {
    lang: "id-ID", hreflang: "id-ID",
    h1: "Terjemahan NAATI Certified {name} — penawaran per dokumen · 5-7 Hari Kerja",
    leads: [
      "Terjemahan NAATI Certified {name} dikerjakan oleh penerjemah tingkat CPT ke atas yang terdaftar NAATI Australia. Setiap set membawa stempel biru NAATI asli plus Certificate of Currency, diterima langsung oleh DHA, VicRoads, Service NSW, AHPRA, USCIS, UKVI, dan IRCC tanpa legalisasi tambahan. penawaran per dokumen per set, dikirim dalam 5-7 hari kerja.",
      "Pesan terjemahan NAATI Certified {name} — tiga paket (Standar PDF / PDF + Hardcopy / Ekspres 5 hari) mencakup seluruh alur migrasi, studi, penukaran SIM, dan registrasi profesi di Australia. Rentang harga transparan penawaran per dokumen, tanpa biaya per halaman.",
      "Aplikasi visa Australia, pendaftaran universitas, atau penukaran SIM meminta terjemahan NAATI Certified {name}? Kami tangani penuh: penerimaan asli, penerjemah CPT khusus, QA dua tahap, stempel biru NAATI, penerbitan Certificate of Currency, pengiriman hardcopy — dalam jendela pasti 5-7 hari kerja.",
    ],
    h2Pool: [
      "Mengapa {name} perlu NAATI Certified",
      "Alur pesan dan pengiriman",
      "Instansi yang menerima terjemahan ini",
      "Yang perlu disiapkan sebelum memesan",
      "Format file dan opsi pengiriman",
      "Harga dan waktu standar",
      "Kasus nyata dari klien",
      "NAATI vs Konsuler vs Sworn Translation",
      "Kesalahan umum yang harus dihindari",
      "Tim penerjemah dan kredensial",
      "Jaminan kualitas dan revisi gratis",
    ],
    paragraphs: [
      "Layanan terjemahan {name} kami sepenuhnya end-to-end: penerimaan asli, analisis jenis dokumen, penugasan ke penerjemah NAATI spesialis, review dua tahap, penstempelan, dan penerbitan Certificate of Currency. Struktur tiga paket penawaran per dokumen memungkinkan Anda memilih berdasarkan urgensi dan metode pengiriman tanpa biaya tersembunyi per baris.",
      "Banyak klien menanyakan mengapa {name} perlu NAATI Certified. NAATI adalah lembaga akreditasi penerjemah milik Pemerintah Australia; setiap tingkat instansi Australia menerima terjemahan NAATI langsung, memangkas biaya dan waktu tunggu berminggu-minggu untuk re-sertifikasi konsuler.",
      "Setiap proyek {name} ditangani oleh NAATI Certified Provisional Translator (CPT) atau di atasnya yang lulus ujian pasangan bahasa dan terikat Kode Etik AUSIT. QA internal mengikuti alur kerja ISO 17100 industri terjemahan — tanpa jalan pintas, tanpa pengiriman tanpa cek.",
      "Konsistensi nama diri sangat penting: nama, alamat, dan instansi penerbit pada {name} harus sama ejaannya dengan dokumen lain dalam bundel pengajuan. Petugas luar negeri selalu membandingkan, dan satu karakter tidak cocok bisa memicu permintaan perbaikan. Saat memesan lebih dari satu dokumen, kami menyelaraskan ejaan di seluruh bundel.",
      "Anda akan diinformasikan setiap tahap via Line, Email, atau WhatsApp: pesanan dikonfirmasi, sedang diterjemahkan, sedang QA, sudah distempel, dikirim. Status terjemahan {name} tidak pernah menjadi misteri dan tidak perlu ditagih.",
      "Selain layanan standar 5-7 hari, kami menawarkan opsi Ekspres 24-48 jam untuk pengajuan visa dan tenggat universitas menit terakhir. Ekspres hanya mengubah prioritas antrean — standar kualitas dan stempel NAATI itu sendiri sama.",
      "Terjemahan {name} berstempel NAATI sendiri tidak memiliki masa kedaluwarsa — stempel mensertifikasi akurasi pada saat penerbitan. Beberapa instansi penerima (misalnya NHS Inggris, sebagian medical board AS) mensyaratkan terjemahan yang diterbitkan dalam 3 atau 6 bulan terakhir — periksa aturan penerima sebelum mengajukan.",
      "Jika Anda menggunakan terjemahan {name} di luar Australia (AS, Kanada, Inggris, negara-negara UE), Anda mungkin memerlukan langkah Apostille atau Legalisasi Konsuler tambahan. Kami dapat memasangkan terjemahan NAATI dengan saluran legalisasi yang sesuai sehingga Anda menerima satu pengiriman terkoordinasi.",
    ],
    faqs: [
      { q: "Apakah terjemahan NAATI Certified {name} diterima Kedutaan Australia di seluruh dunia?", a: "Ya. NAATI adalah akreditasi milik Pemerintah Australia sendiri. Setiap misi terafiliasi DFAT, pusat pemrosesan DHA, dan instansi negara bagian Australia menerima terjemahan berstempel biru NAATI yang masih berlaku secara langsung." },
      { q: "Apakah {name} dapat diterjemahkan ke bahasa selain Inggris?", a: "Ya. Standar kami Thai⇄Inggris; pasangan Thai⇄Mandarin Sederhana, Thai⇄Jepang, Thai⇄Korea, dan Thai⇄Vietnam tersedia dengan stempel NAATI yang sesuai." },
      { q: "Berapa lama stempel NAATI pada {name} berlaku?", a: "Stempel itu sendiri tidak kedaluwarsa. Sebagian instansi (mis. NHS Inggris, medical board AS tertentu) mensyaratkan terjemahan yang diterbitkan dalam 3 atau 6 bulan — selalu periksa aturan penerima." },
      { q: "Bisakah hardcopy {name} dikirim ke Australia?", a: "Bisa. Pengiriman terdaftar di dalam Thailand, dan DHL/UPS ke Australia, Inggris, AS, dan setiap negara UE. Nomor pelacakan diberikan saat pengiriman." },
      { q: "Bagaimana jika instansi penerima meminta koreksi pada {name}?", a: "Koreksi yang timbul dari klarifikasi penerima (mis. transliterasi alternatif) gratis dalam 30 hari sejak pengiriman. Kami menerbitkan ulang dengan tanggal stempel baru dan mengoordinasikan pengiriman ulang bila hardcopy diperlukan." },
    ],
    howto: [
      { tag: "Kirim", text: "Kirim scan atau foto ponsel jelas (disarankan 300 dpi) dari {name} asli via Line / Email." },
      { tag: "Konfirmasi", text: "Kami konfirmasi paket, harga, dan jendela pengiriman dalam dua jam kerja." },
      { tag: "Terjemah", text: "Penerjemah NAATI tingkat CPT memproduksi file bahasa target; linguis kedua menjalankan QA." },
      { tag: "Stempel", text: "Penerjemah membubuhkan stempel biru NAATI dan menerbitkan Certificate of Currency." },
      { tag: "Kirim", text: "Anda menerima PDF via Email plus, bila dipilih, hardcopy via kurir terdaftar." },
    ],
    metaTitle: "Pesan Terjemahan NAATI Online — {name} | Thai Notary Law",
    metaDesc: "Ajukan terjemahan NAATI {name} secara online. Staf memeriksa dokumen dan mengirim penawaran; penerimaan diputuskan oleh instansi tujuan.",
    ui: {
      breadcrumbStore: "Toko", breadcrumbHome: "Beranda",
      priceAndPackages: "Harga dan paket",
      acceptedBy: "Diterima oleh",
      howToOrder: "Cara memesan",
      faqs: "Pertanyaan yang sering diajukan",
      stepOf: "Langkah {i} · {tag}",
      ratingReviews: "★ {v} · {n} ulasan",
      naatiStamp: "Stempel Biru NAATI Asli",
      coc: "Certificate of Currency",
    },
  },
};
